// AGENT-REPORTING (13 §Review of consequential agent-reported numbers): a `kpi_value` approval
// as a review card — the metric, the seat, the period, the reported value (or event quantity)
// with its unit, the agent's evidence (text and link) and the run that reported it — with
// Approve, Edit value (a number: `approvals.edit` with only `value` / `quantity` changed) and
// Reject (a reason). The decision records the value in the same request; nothing counts before.
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckIcon, ExternalLinkIcon, PencilIcon } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { ApprovalRow } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { problemText } from "../de/de-status";
import { formatDateTime } from "../format";
import { formatElapsed } from "./agent-parts";
// AGT-UI-2: a KPI review's decision refreshes what every approval decision does.
import { invalidateDecided } from "./agent-queries";

/** `proposal.change` of a `kpi_value` approval (the contract's `KpiValueChange`). */
export type KpiValueChange = {
  metric_def_id: string;
  metric_key: string;
  metric_name: string;
  seat_id: string;
  seat_title: string;
  kind: "value" | "event";
  value: number | null;
  quantity: number | null;
  unit: string;
  period_type: string;
  period_key: string;
  occurred_at: string | null;
  event_type: string | null;
  provenance: {
    run_id: string;
    agent_membership_id: string;
    agent_name: string | null;
    evidence_text: string;
    evidence_url: string | null;
  };
};

export const kpiChangeOf = (a: ApprovalRow): KpiValueChange =>
  a.proposal.change as unknown as KpiValueChange;

const UNIT_WORD: Record<string, string> = {
  number: "",
  percent: "%",
  currency: "",
  time: "min",
  rating: "/ 5",
};

/** "3" · "3 events" · "45%" */
export function kpiReported(c: KpiValueChange, value = c.kind === "event" ? c.quantity : c.value) {
  if (value === null) return "—";
  if (c.kind === "event") return `${value} ${value === 1 ? "event" : "events"}`;
  const unit = UNIT_WORD[c.unit] ?? c.unit;
  return unit === "%" ? `${value}%` : unit ? `${value} ${unit}` : String(value);
}

const shortRun = (id: string) => id.replaceAll("-", "").slice(-8);

export function KpiReviewCard({
  api,
  company,
  approval: a,
  requester,
  reject,
  decide = true,
}: {
  api: Api;
  company: CompanyAccess;
  approval: ApprovalRow;
  requester: string;
  /** The screen's reject dialog (a reason is required). */
  reject: ReactNode;
  /** Approve / Edit value / Reject on the card (the detail page has them in its header). */
  decide?: boolean;
}) {
  const qc = useQueryClient();
  const c = kpiChangeOf(a);
  const approve = useMutation({
    mutationFn: () => api.client.approvals.approve({ params: { co: company.id, id: a.id } }),
    onSettled: () => invalidateDecided(qc, company.id),
  });
  const p = c.provenance;
  const agent = p?.agent_name ?? requester;
  const edited =
    a.state === "edited" ? (a.approved_payload?.change as KpiValueChange | undefined) : undefined;
  return (
    <Card data-slot="kpi-review" data-approval={a.id} data-state={a.state}>
      <CardHeader>
        <CardTitle className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <Link
            to="/c/$slug/agents/approvals/$id"
            params={{ slug: company.slug, id: a.id }}
            search={keepFocus}
            className="min-w-0 break-words underline-offset-4 hover:underline"
          >
            {c.metric_name}
          </Link>
          <Badge variant="outline">KPI value review</Badge>
          {a.state !== "pending" ? (
            <Badge variant={a.state === "rejected" ? "danger" : "success"}>
              {a.state === "edited"
                ? "Approved with edits"
                : a.state === "approved"
                  ? "Approved"
                  : a.state === "rejected"
                    ? "Rejected"
                    : "Expired"}
            </Badge>
          ) : (
            <Badge variant="info" dot>
              Pending review
            </Badge>
          )}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          {c.seat_title} · {c.period_key} · reported by {agent} {formatElapsed(a.created_at)} ago
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span
            className="font-heading text-2xl font-title tabular-nums text-foreground"
            data-slot="kpi-review-value"
          >
            {kpiReported(
              c,
              edited ? (edited.kind === "event" ? edited.quantity : edited.value) : undefined,
            )}
          </span>
          {edited ? (
            <span className="text-sm text-muted-foreground">
              (the agent reported {kpiReported(c)})
            </span>
          ) : null}
          <span className="text-xs text-muted-foreground">
            {c.kind === "event" ? `events of ${c.event_type ?? c.metric_key}` : c.metric_key}
          </span>
        </div>
        {p ? (
          <blockquote
            className="border-l-2 border-border pl-3 text-sm break-words text-foreground"
            data-slot="kpi-review-evidence"
          >
            “{p.evidence_text}”
          </blockquote>
        ) : null}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          {p?.evidence_url ? (
            <a
              href={p.evidence_url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-primary-text underline-offset-4 hover:underline"
            >
              <ExternalLinkIcon aria-hidden="true" className="size-3.5" />
              Evidence
            </a>
          ) : (
            <span className="text-muted-foreground">No evidence link</span>
          )}
          {a.run_id ? (
            <Link
              to="/c/$slug/agents/runs/$id"
              params={{ slug: company.slug, id: a.run_id }}
              search={keepFocus}
              className="text-primary-text underline-offset-4 hover:underline"
            >
              View run {shortRun(a.run_id)}
            </Link>
          ) : null}
          {a.decided_at ? (
            <span className="text-muted-foreground">Decided {formatDateTime(a.decided_at)}</span>
          ) : null}
          {a.reason ? <span className="text-muted-foreground">Reason: {a.reason}</span> : null}
        </div>
        {approve.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(approve.error, "The value could not be approved.")}
          </p>
        ) : null}
      </CardContent>
      {decide && a.state === "pending" ? (
        <CardFooter className="flex flex-wrap gap-2">
          <Button
            disabled={approve.isPending}
            onClick={() => approve.mutate()}
            aria-label={`Approve ${c.metric_name}: ${kpiReported(c)} for ${c.period_key}`}
          >
            <CheckIcon aria-hidden="true" />
            {approve.isPending ? "Approving…" : "Approve"}
          </Button>
          <KpiEditDialog api={api} company={company} approval={a} />
          {reject}
        </CardFooter>
      ) : null}
    </Card>
  );
}

/** Edit value: the supervisor's number is recorded; the agent's is kept as `original_value`. */
export function KpiEditDialog({
  api,
  company,
  approval: a,
  size = "default",
}: {
  api: Api;
  company: CompanyAccess;
  approval: ApprovalRow;
  size?: "default" | "lg";
}) {
  const qc = useQueryClient();
  const c = kpiChangeOf(a);
  const field = c.kind === "event" ? "quantity" : "value";
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(String(c[field] ?? ""));
  const id = useId();
  const n = Number(text);
  const ok = text.trim() !== "" && Number.isFinite(n) && (field === "value" || n >= 0);
  const edit = useMutation({
    mutationFn: () =>
      api.client.approvals.edit({
        params: { co: company.id, id: a.id },
        body: {
          proposal: {
            ...a.proposal,
            change: { ...a.proposal.change, [field]: n },
          },
        },
      }),
    onSuccess: () => setOpen(false),
    onSettled: () => invalidateDecided(qc, company.id),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="secondary" size={size} onClick={() => setOpen(true)}>
        <PencilIcon aria-hidden="true" />
        Edit value
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {c.metric_name}</DialogTitle>
          <DialogDescription>
            Your number is recorded for {c.period_key}; the agent's {kpiReported(c)} stays on the
            record as what it reported.
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor={id}>{field === "quantity" ? "Quantity" : "Value"}</FieldLabel>
          <Input
            id={id}
            type="number"
            inputMode="decimal"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <FieldDescription>
            {c.seat_title} · {c.period_key}
          </FieldDescription>
        </Field>
        {edit.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(edit.error, "The value could not be saved.")}
          </p>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button disabled={!ok || edit.isPending} onClick={() => edit.mutate()}>
            {edit.isPending ? "Saving…" : "Approve with this value"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
